import { useState, useEffect } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { PencilSimple, Check, CircleNotch, Wrench } from "@phosphor-icons/react";

/**
 * Admin-only inline manager for the Universal Technical Director prompt.
 * TECHNICAL-ONLY global guidance appended to Creative Director Pure Text Mode
 * generations (and any future mode that opts in). Persisted server-side via
 * /api/settings/technical-director. Empty string clears it.
 *
 * Kept separate from the legacy "default direction" — the default direction
 * may contain creative instructions that cap premium prompts, whereas the
 * Universal Technical Director must remain technical only.
 */
export default function TechnicalDirectorManager() {
  const [prompt, setPrompt] = useState("");
  const [updatedBy, setUpdatedBy] = useState("");
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get("/settings/technical-director").then((r) => {
      setPrompt(r.data?.prompt || "");
      setUpdatedBy(r.data?.updated_by || "");
    }).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/settings/technical-director", { value: (draft || "").trim() });
      setPrompt(r.data.prompt);
      toast.success("Universal Technical Director updated.");
      setEditing(false); setDraft("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update the Technical Director.");
    } finally { setSaving(false); }
  };

  return (
    <section
      data-testid="technical-director-manager"
      className="mb-8 rounded-xl border border-white/10 bg-[#0E0E0E] p-4 flex items-start gap-3 flex-wrap"
    >
      <Wrench size={18} weight="duotone" className="text-cyan-300 shrink-0 mt-1" />
      <div className="min-w-0 flex-1">
        <p className="text-xs uppercase tracking-wider text-zinc-500 font-bold">
          Universal Technical Director (admin)
        </p>
        <p className="text-[11px] text-zinc-500 mt-0.5">
          Technical-only global quality prompt appended in Creative Director Pure Text Mode.
        </p>
        {!editing ? (
          <p className="text-sm text-zinc-300 mt-2 whitespace-pre-wrap break-words">
            <span data-testid="technical-director-current" className="text-white">
              {prompt || <span className="text-zinc-600 italic">(not set — no technical direction appended)</span>}
            </span>
            {updatedBy && <span className="ml-2 text-[11px] text-zinc-500">· set by {updatedBy}</span>}
          </p>
        ) : (
          <textarea
            data-testid="technical-director-input"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="e.g. Render at print-quality resolution. Use correct kerning. Avoid text artefacts on supplied words."
            rows={4}
            className="mt-2 w-full rounded-md bg-black/60 border border-white/10 px-3 py-2 text-sm text-white outline-none focus:border-cyan-400/60 resize-y"
            onKeyDown={(e) => { if (e.key === "Escape") { setEditing(false); setDraft(""); } }}
            autoFocus
          />
        )}
      </div>
      {!editing ? (
        <button
          data-testid="technical-director-edit"
          onClick={() => { setDraft(prompt); setEditing(true); }}
          className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-white/5 hover:bg-white/10 text-sm text-zinc-300 hover:text-white transition-colors"
        >
          <PencilSimple size={14} weight="bold" /> Change
        </button>
      ) : (
        <div className="flex items-center gap-2 mt-1">
          <button
            data-testid="technical-director-save"
            disabled={saving}
            onClick={save}
            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-cyan-400 hover:bg-cyan-300 text-black text-sm font-bold disabled:opacity-60"
          >
            {saving ? <CircleNotch size={14} className="animate-spin" /> : <Check size={14} weight="bold" />} Save
          </button>
          <button
            data-testid="technical-director-cancel"
            onClick={() => { setEditing(false); setDraft(""); }}
            className="h-8 px-3 rounded-full text-sm text-zinc-400 hover:text-white"
          >Cancel</button>
        </div>
      )}
    </section>
  );
}
